import { alpha, Box, SxProps, Theme, useTheme } from '@mui/material';
import { FC, useEffect, useState } from 'react';

interface AppAvatarProps {
  src: string;
  isAbsent: boolean;
  alt: string;
  sx?: SxProps<Theme>;
}

const AppAvatar: FC<AppAvatarProps> = ({
  src,
  isAbsent = false,
  alt = '',
  sx,
}) => {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const theme = useTheme();

  useEffect(() => {
    if (!src) return;

    let objectUrl: string | null = null;

    const fetchImage = async () => {
      try {
        const accessToken = localStorage.getItem('accessToken');
        if (!accessToken) throw new Error('No access token found');

        const response = await fetch(src, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch image: ${response.status}`);
        }

        const imageBlob = await response.blob();
        objectUrl = URL.createObjectURL(imageBlob);
        setImageUrl(objectUrl);
      } catch (err: unknown) {
        // empty
      }
    };

    fetchImage();

    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [src]);

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        border: `1px solid ${theme.palette.secondary.main}`,
        backgroundColor: isAbsent
          ? alpha(theme.palette.common.grey, 0.8)
          : 'transparent',
        boxSizing: 'border-box',
        position: 'relative',
        ...sx,
      }}
    >
      <Box
        component='img'
        src={imageUrl ?? ''}
        sx={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          filter: isAbsent ? 'grayscale(100%)' : 'none',
        }}
        alt={alt}
      />

      {isAbsent && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundColor: alpha(theme.palette.common.grey, 0.8),
          }}
        />
      )}
    </Box>
  );
};
export default AppAvatar;
